import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@env/environment';
import { deserialize } from '@shared/models/base-helper';
import { AcrProject } from '@shared/models/acr-project.model';

interface QueryResponse {
  results: any[];
  total: number;
}

interface DeserializedResponse {
  results: AcrProject[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class AcrProjectService {
  apiUrl = environment.apiUrl + '/acr_projects/';

  constructor(readonly http: HttpClient) {}

  getFilteredProjects(filterString: string = '?sort=id(desc)'): Observable<DeserializedResponse> {
    const filterUrl = this.apiUrl + filterString;
    return this.http.get<QueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedResponse = {
          results: [],
          total: response.total,
        };
        for (const project of response.results) {
          deserializedResponse.results.push(deserialize(project, AcrProject));
        }
        return deserializedResponse;
      })
    );
  }

  getAllProjects(): Observable<AcrProject[]> {
    return this.http.get<QueryResponse>(this.apiUrl).pipe(
      map(response => deserialize(response.results, AcrProject)));
  }

  getProject(id: number) {
    return this.http.get<QueryResponse>(`${this.apiUrl}${id}/`).pipe(
      map(response => deserialize(response, AcrProject)));
  }

  createProject(project: any): Observable<AcrProject> {
    return this.http.post<any>(`${this.apiUrl}`, project)
      .pipe(map((project: any) => deserialize(project, AcrProject)));
  }

  updateProject(project: any): Observable<AcrProject> {
    return this.http
      .put<any>(`${this.apiUrl}${project.id}`, project)
      .pipe(map((project: any) => deserialize(project, AcrProject)));
  }

  downloadProjectStrata(project: number) {
    return this.http.get(`${this.apiUrl}${project}/export_strata/`).pipe(
      map((response: any) => {
        return response;
      }));
  }

  downloadEnrolledArea(project: number) {
    return this.http.get(`${this.apiUrl}${project}/export_enrolled_area/`).pipe(
      map((response: any) => {
        return response;
      }));
  }

  updateReportingPeriod(acrProjectId: number, reportingPeriodId: number, period: any) {
    return this.http
      .patch<any>(`${this.apiUrl}${acrProjectId}/reporting_period/${reportingPeriodId}/`, period)
      .pipe(
        map((project: any) => deserialize(project, AcrProject)),
        catchError(error => {
          return throwError(error);
        })
      );

  }

  recalculateReportingPeriods(id: number) {
    return this.http
      .post<any>(`${this.apiUrl}${id}/recalculate_reporting_periods/`, {})
      .pipe(
        map((project: any) => deserialize(project, AcrProject)),
        catchError(error => {
          return throwError(error);
        })
      );
  }
}
